import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Maximize2, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Individual remote video/audio tile
function RemoteTile({ stream, label, showVideo }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) videoRef.current.srcObject = stream;
  }, [stream]);

  useEffect(() => {
    if (audioRef.current && stream) audioRef.current.srcObject = stream;
  }, [stream]);

  return (
    <div style={{
      position: 'relative', background: '#222', borderRadius: 8, overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0,
    }}>
      {/* Hidden audio element — always plays regardless of video */}
      <audio ref={audioRef} autoPlay playsInline />
      {showVideo ? (
        <video ref={videoRef} autoPlay playsInline style={{
          width: '100%', height: '100%', objectFit: 'contain',
        }} />
      ) : (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#fff',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: 'var(--c-brand)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700,
          }}>
            {label?.[0]?.toUpperCase() || '?'}
          </div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        </div>
      )}
      {/* Name overlay */}
      {showVideo && (
        <div style={{
          position: 'absolute', bottom: 6, left: 8, color: '#fff', fontSize: 11, fontWeight: 600,
          background: 'rgba(0,0,0,0.5)', padding: '2px 8px', borderRadius: 4,
        }}>
          {label}
        </div>
      )}
    </div>
  );
}

export default function ActiveCallOverlay({ callState, onEndCall, onToggleVideo, onToggleAudio, conversations }) {
  const { t } = useTranslation();
  const localVideoRef = useRef(null);
  const [expanded, setExpanded] = useState(false);

  const formatDuration = (secs) => {
    if (!secs) return '0:00';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Auto-expand for video calls once active
  useEffect(() => {
    if (callState?.callType === 'video' && callState?.status === 'active') {
      setExpanded(true);
    }
  }, [callState?.callType, callState?.status]);

  // Attach local stream
  useEffect(() => {
    if (localVideoRef.current && callState?.localStream) {
      localVideoRef.current.srcObject = callState.localStream;
    }
  }, [callState?.localStream, callState?.videoEnabled, expanded]);

  if (!callState) return null;

  const { status, callType, videoEnabled, audioEnabled, duration, conversationId, remoteStreams = {} } = callState;
  const remoteVideoStates = callState.remoteVideoStates || {};
  const isVideo = callType === 'video' || videoEnabled;
  const remoteEntries = Object.entries(remoteStreams); // [[userId, stream], ...]
  const remoteCount = remoteEntries.length;

  // Find conversation name
  const conv = conversations?.find(c => c.id === conversationId);
  const calleeName = conv?.type === 'dm'
    ? `${conv.dm_user?.firstName || ''} ${conv.dm_user?.lastName || ''}`.trim()
    : conv?.name || '';

  // Build participant label map from conversation members
  const memberMap = {};
  if (conv?.members) {
    conv.members.forEach(m => { memberMap[m.id || m.user_id] = `${m.firstName || m.first_name || ''} ${m.lastName || m.last_name || ''}`.trim(); });
  }
  if (conv?.dm_user) {
    memberMap[conv.dm_user.id] = `${conv.dm_user.firstName || ''} ${conv.dm_user.lastName || ''}`.trim();
  }

  const statusText = status === 'ringing' ? t('chat.calling') :
    status === 'connecting' ? t('chat.connecting') :
    status === 'active' ? formatDuration(duration) : '';

  // Display name: for DM use calleeName, for groups show participant count
  const barName = conv?.type === 'dm' ? calleeName : (
    remoteCount > 0 ? `${calleeName} (${remoteCount + 1})` : calleeName
  );

  // Compact bar mode (audio-only or not expanded)
  if (!isVideo || !expanded) {
    return ReactDOM.createPortal((
      <div className="active-call-bar" style={{
        position: 'fixed', top: 57, left: '50%', transform: 'translateX(-50%)',
        zIndex: 250, display: 'flex', alignItems: 'center', gap: 12,
        background: status === 'active' ? '#22c55e' : 'var(--c-brand)',
        color: '#fff', padding: '8px 16px', borderRadius: '0 0 12px 12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)', minWidth: 280,
        transition: 'background 0.3s',
      }}>
        {isVideo ? <Video size={16} /> : <Phone size={16} />}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {barName}
          </div>
          <div style={{ fontSize: 11, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>
            {statusText}
            {status === 'ringing' && <span className="call-dots">...</span>}
          </div>
        </div>

        {/* Hidden audio elements for all remote streams */}
        {remoteEntries.map(([uid, stream]) => (
          <AudioPlayer key={uid} stream={stream} />
        ))}

        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onToggleAudio} style={controlBtnStyle}
            title={audioEnabled ? t('chat.muteMic') : t('chat.unmuteMic')}>
            {audioEnabled ? <Mic size={14} /> : <MicOff size={14} />}
          </button>

          {(callType === 'video' || videoEnabled) && (
            <button onClick={onToggleVideo} style={controlBtnStyle}
              title={videoEnabled ? t('chat.cameraOff') : t('chat.cameraOn')}>
              {videoEnabled ? <Video size={14} /> : <VideoOff size={14} />}
            </button>
          )}

          {isVideo && (
            <button onClick={() => setExpanded(true)} style={controlBtnStyle}>
              <Maximize2 size={14} />
            </button>
          )}

          <button onClick={onEndCall} style={{
            ...controlBtnStyle, background: '#ef4444',
          }}>
            <PhoneOff size={14} />
          </button>
        </div>
      </div>
    ), document.body);
  }

  // Grid template based on remote count
  const gridStyle = remoteCount <= 1
    ? { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' }
    : remoteCount === 2
    ? { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' }
    : { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }; // 3-4 → 2×2

  // Expanded video mode with grid
  return ReactDOM.createPortal((
    <div style={{
      position: 'fixed', inset: 0, zIndex: 250, background: '#111',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Remote video grid */}
      <div style={{
        flex: 1, display: 'grid', gap: 4, padding: 4, minHeight: 0,
        ...gridStyle,
      }}>
        {remoteEntries.length > 0 ? remoteEntries.map(([uid, stream]) => (
          <RemoteTile
            key={uid}
            stream={stream}
            label={memberMap[uid] || uid.substring(0, 8)}
            showVideo={remoteVideoStates[uid] !== false}
          />
        )) : (
          // No remote streams yet — show waiting state
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, color: '#fff',
          }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%', background: 'var(--c-brand)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700,
            }}>
              {calleeName?.[0]?.toUpperCase() || '?'}
            </div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{calleeName}</div>
            <div style={{ fontSize: 13, opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{statusText}</div>
          </div>
        )}
      </div>

      {/* Local video (PiP) */}
      {videoEnabled && (
        <div style={{
          position: 'absolute', bottom: 100, right: 16, width: 160, height: 120,
          borderRadius: 8, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.3)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)', zIndex: 2,
        }}>
          <video ref={localVideoRef} autoPlay playsInline muted style={{
            width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)',
          }} />
        </div>
      )}

      {/* Bottom controls */}
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16,
        padding: '20px 0 32px', flexShrink: 0,
        background: 'rgba(0,0,0,0.7)',
      }}>
        <button onClick={onToggleAudio} style={{
          ...expandedControlBtn, background: audioEnabled ? 'rgba(255,255,255,0.2)' : '#ef4444',
        }}>
          {audioEnabled ? <Mic size={20} /> : <MicOff size={20} />}
        </button>

        <button onClick={onToggleVideo} style={{
          ...expandedControlBtn, background: videoEnabled ? 'rgba(255,255,255,0.2)' : '#ef4444',
        }}>
          {videoEnabled ? <Video size={20} /> : <VideoOff size={20} />}
        </button>

        <button onClick={() => setExpanded(false)} style={expandedControlBtn}>
          <Minimize2 size={20} />
        </button>

        <button onClick={onEndCall} style={{
          ...expandedControlBtn, background: '#ef4444', width: 56, height: 56,
        }}>
          <PhoneOff size={22} />
        </button>
      </div>

      {/* Duration display */}
      <div style={{
        position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
        color: '#fff', fontSize: 14, fontWeight: 600, background: 'rgba(0,0,0,0.5)',
        padding: '4px 12px', borderRadius: 20, fontVariantNumeric: 'tabular-nums',
      }}>
        {statusText}
      </div>
    </div>
  ), document.body);
}

// Hidden audio-only player (for compact bar mode)
function AudioPlayer({ stream }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);
  return <audio ref={ref} autoPlay playsInline style={{ display: 'none' }} />;
}

const controlBtnStyle = {
  width: 32, height: 32, borderRadius: '50%', border: 'none',
  background: 'rgba(255,255,255,0.2)', color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer',
};

const expandedControlBtn = {
  width: 48, height: 48, borderRadius: '50%', border: 'none',
  background: 'rgba(255,255,255,0.2)', color: '#fff',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', transition: 'background 0.15s',
};
